import { h } from 'preact'
import { useState } from 'preact/hooks'

import type { QaPageStatus, QaScenario, QaTriColor } from './types'
import type { QaStrings } from './i18n'
import { LaneIcons } from './LaneIcons'
import {
  aggregateTri,
  botTriColor,
  formatQaDate,
  humanTriColor,
  QA_CASE_STATUS_LABEL_KEY,
  QA_GROUNDING_LABEL_KEY,
  QA_PRIORITY_MODIFIER,
  shortVersion,
} from './ui'

/**
 * Preact port of the donor `qa-test-panel.vue`.
 *
 * Presentational (no data-fetching, router, or store). The container task feeds
 * it the resolved page-status, a `state` discriminator (so the two new empty
 * states can render even when `page` is null), and the run metadata for the
 * freshness banner. Local UI state (which suite groups are expanded) lives in a
 * `useState` hook — matching the donor's `expanded`/`toggleGroup` interaction.
 *
 * The donor's `<script setup>` computed properties (lane tri-color derivation and
 * suite grouping) are extracted here as the pure exported helpers `laneTriColor`
 * and `groupScenarios` so they're unit-testable and reusable by the mount task.
 */
export interface TestPanelProps {
  page: QaPageStatus | null
  state: 'known' | 'no-tests' | 'unknown-page'
  meta: { generated_at: string; app_version: string; environment: string }
  strings: QaStrings
}

/** The three independent lane tri-colors of a scenario — single source of the
 *  dev/bot/human derivation (donor `lanesFor`). The client lane with no event is
 *  gray (never green), because `humanTriColor` returns gray for an unvalidated lane. */
export interface ScenarioLanes {
  dev: QaTriColor
  bot: QaTriColor
  human: QaTriColor
}

export function laneTriColor(scenario: QaScenario): ScenarioLanes {
  return {
    dev: humanTriColor(scenario.dev),
    bot: botTriColor(scenario.automated),
    human: humanTriColor(scenario.client),
  }
}

export interface ScenarioGroupView {
  key: string
  label: string
  scenarios: QaScenario[]
}

/** Group scenarios by `scenario.group.key` (donor `grouped`). Scenarios with no
 *  group fall into a `'·'` bucket. Keys are alphabetical with the inter-module
 *  `X` suite forced last, matching the donor ordering. */
export function groupScenarios(scenarios: ReadonlyArray<QaScenario>): ScenarioGroupView[] {
  const map = new Map<string, QaScenario[]>()
  for (const scenario of scenarios) {
    const key = scenario.group?.key ?? '·'
    const bucket = map.get(key) ?? []
    bucket.push(scenario)
    map.set(key, bucket)
  }
  const keys = [...map.keys()].sort((a, b) => (a === 'X' ? 1 : b === 'X' ? -1 : a.localeCompare(b)))
  return keys.map((key) => {
    const bucket = map.get(key)!
    return { key, label: bucket[0]?.group?.label ?? '', scenarios: bucket }
  })
}

function interpolate(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k: string) => String(params[k] ?? ''))
}

/** One scenario row: title, priority chip, provenance + tags, and the three lanes. */
function ScenarioRow({ scenario, strings }: { scenario: QaScenario; strings: QaStrings }) {
  const lanes = laneTriColor(scenario)
  const provenance = scenario.automated.provenance
  return h(
    'li',
    { class: 'qa-row', 'data-testid': 'qa-scenario-row' },
    h(
      'span',
      { class: 'qa-row__title' },
      scenario.priority
        ? h(
            'span',
            { class: `qa-tag ${QA_PRIORITY_MODIFIER[scenario.priority]}`, 'data-testid': 'qa-priority' },
            scenario.priority,
          )
        : null,
      h('span', { class: 'qa-row__name' }, scenario.title),
      scenario.source === 'testgen'
        ? h('span', { class: 'qa-tag', title: strings['source.testgenHint'] }, strings['source.testgen'])
        : null,
      scenario.caseStatus
        ? h('span', { class: 'qa-tag' }, strings[QA_CASE_STATUS_LABEL_KEY[scenario.caseStatus]])
        : null,
      scenario.grounding
        ? h('span', { class: 'qa-tag' }, strings[QA_GROUNDING_LABEL_KEY[scenario.grounding]])
        : null,
      provenance ? h('span', { class: 'qa-tag', 'data-testid': 'qa-provenance' }, provenance) : null,
    ),
    h(LaneIcons, { dev: lanes.dev, bot: lanes.bot, human: lanes.human, strings }),
  )
}

export function TestPanel({ page, state, meta, strings }: TestPanelProps) {
  // Local UI state: which suite-group keys are expanded. Matches the donor's
  // `expanded` ref, which starts empty → all groups COLLAPSED by default (so a
  // heavily-loaded page folds into a few theme headers, per QaScenarioGroup).
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const toggleGroup = (key: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const freshness = interpolate(strings['panel.freshness'], {
    date: formatQaDate(meta.generated_at),
    version: shortVersion(meta.app_version),
    env: meta.environment,
  })

  let body
  if (state === 'no-tests') {
    body = h('p', { class: 'qa-panel__empty' }, strings['panel.noTests'])
  } else if (state === 'unknown-page') {
    body = h('p', { class: 'qa-panel__empty' }, strings['panel.unknownPage'])
  } else {
    const scenarios = page?.scenarios ?? []
    const hasGroups = scenarios.some((s) => s.group)
    if (scenarios.length === 0) {
      body = h('p', { class: 'qa-panel__empty' }, strings['panel.noScenarios'])
    } else if (!hasGroups) {
      // Synthetic / ungrouped page (donor `hasGroups === false`): flat list, no
      // suite header — avoids a spurious empty-label group wrapper.
      body = h(
        'ul',
        { class: 'qa-panel__list' },
        scenarios.map((scenario) => h(ScenarioRow, { key: scenario.id, scenario, strings })),
      )
    } else {
      const groups = groupScenarios(scenarios)
      body = h(
        'ul',
        { class: 'qa-panel__groups', 'data-testid': 'qa-suite-groups' },
        groups.map((group) => {
          const isOpen = expanded.has(group.key)
          const agg = {
            dev: aggregateTri(group.scenarios.map((s) => humanTriColor(s.dev))),
            bot: aggregateTri(group.scenarios.map((s) => botTriColor(s.automated))),
            human: aggregateTri(group.scenarios.map((s) => humanTriColor(s.client))),
          }
          return h(
            'li',
            { key: group.key, class: 'qa-suite', 'data-testid': 'qa-panel-group' },
            h(
              'button',
              {
                type: 'button',
                class: 'qa-suite__head',
                'aria-expanded': isOpen,
                'data-testid': 'qa-suite-group',
                onClick: () => toggleGroup(group.key),
              },
              h(
                'span',
                { class: `qa-suite__chev ${isOpen ? 'is-open' : ''}`, 'aria-hidden': 'true' },
                '▸',
              ),
              h('span', { class: 'qa-suite__key' }, group.key),
              h('span', { class: 'qa-suite__label' }, group.label),
              h(
                'span',
                { class: 'qa-suite__count' },
                interpolate(strings['suite.cases'], { count: group.scenarios.length }),
              ),
              h(LaneIcons, { dev: agg.dev, bot: agg.bot, human: agg.human, strings }),
            ),
            isOpen
              ? h(
                  'ul',
                  { class: 'qa-suite__list' },
                  group.scenarios.map((scenario) =>
                    h(ScenarioRow, { key: scenario.id, scenario, strings }),
                  ),
                )
              : null,
          )
        }),
      )
    }
  }

  return h(
    'section',
    { class: 'qa-panel', 'data-testid': 'qa-panel' },
    body,
    h(
      'footer',
      { class: 'qa-panel__foot' },
      h(
        'span',
        { class: 'qa-panel__count', title: freshness, 'data-testid': 'qa-panel-freshness' },
        freshness,
      ),
    ),
  )
}
